import { PRODUTO_PADRAO } from "@nhomi/core";

/* ============================================================
   Comprar pela Google Play, de dentro da página

   O Nhomi na loja é um TWA: a mesma página que roda em nhomi.app,
   embrulhada num pacote Android. Não existe código nativo, então não
   existe SDK de cobrança — o que existe é a Digital Goods API, que o
   Chrome expõe SÓ quando a página está rodando dentro do TWA.

   ISSO É A HISTÓRIA INTEIRA DE "WEB NÃO COBRA". Não é decisão de
   produto nem coisa que dá para ligar depois com uma flag: no
   nhomi.app aberto pelo Chrome, `getDigitalGoodsService` não existe.
   Por isso tudo aqui começa por `disponivel()`, e por isso a tela de
   assinar tem um caminho inteiro para quando a resposta é não.

   E POR QUE ISSO CUSTA UM ENVIO NOVO À LOJA. A Digital Goods API só
   aparece se o pacote tiver sido gerado com `playBilling` ligado — está
   em `android/twa-manifest.json`. É a primeira coisa do projeto que não
   se resolve com um deploy do site: mexer aqui e publicar o site não
   basta, precisa de pacote novo e de revisão.
   ============================================================ */

/** O "método de pagamento" que identifica a loja para a Payment Request API. */
const METODO = "https://play.google.com/billing";

/* A Digital Goods API não está no lib.dom do TypeScript. O que está
   abaixo é só o pedaço que este arquivo usa — declarar a interface
   inteira seria copiar uma especificação que muda sem avisar. */

export interface DetalhesDoItem {
  itemId: string;
  title: string;
  description: string;
  price: { currency: string; value: string };
  /** ISO 8601, tipo "P1M" para mensal. Vem do cadastro no Play Console. */
  subscriptionPeriod?: string;
}

interface Compra { itemId: string; purchaseToken: string }

interface ServicoDaLoja {
  getDetails(ids: string[]): Promise<DetalhesDoItem[]>;
  listPurchases(): Promise<Compra[]>;
}

type ComGetDigitalGoods = Window & {
  getDigitalGoodsService?: (metodo: string) => Promise<ServicoDaLoja>;
};

/**
 * Dá para comprar aqui?
 *
 * Duas perguntas em uma, e as duas precisam ser sim. A primeira é se a
 * API existe (só dentro do TWA). A segunda é se a loja responde — um
 * pacote gerado sem `playBilling`, um aparelho sem Play Store, ou uma
 * conta Google sem forma de pagamento fazem o serviço não abrir, e aí a
 * API existe mas não serve para nada.
 *
 * Perguntar as duas juntas evita a pior tela possível: um botão
 * "Assinar" que existe, aceita o toque e morre sem explicação.
 */
export async function servico(): Promise<ServicoDaLoja | null> {
  const janela = window as ComGetDigitalGoods;
  if (typeof janela.getDigitalGoodsService !== "function") return null;
  try {
    return await janela.getDigitalGoodsService(METODO);
  } catch {
    // A API recusa com exceção quando o método não é suportado. Não é
    // erro nosso e não tem o que mostrar — é o caso "aqui não vende".
    return null;
  }
}

export const disponivel = async (): Promise<boolean> => (await servico()) !== null;

/**
 * Dá pra COMPRAR aqui? É uma pergunta diferente de "a loja responde?".
 *
 * Consultar e comprar viajam por caminhos Android SEPARADOS, e descobrir
 * isso custou quatro pacotes:
 *
 *   - `getDetails` e `listPurchases` (Digital Goods API) passam pelo
 *     `DelegationService` do app, que o Chrome procura e liga por conta
 *     própria;
 *   - a COMPRA passa pela `PaymentActivity`, por um intent Android
 *     (`org.chromium.intent.action.PAY`) que não encosta naquele serviço.
 *
 * No aparelho onde isto foi medido a primeira ponte estava quebrada
 * (`clientAppUnavailable` em toda chamada) e a segunda respondia
 * `canMakePayment() === true`. Ou seja: dava pra vender, e a tela recusava
 * porque não tinha conseguido ler o PREÇO — que é enfeite perto de poder
 * cobrar.
 *
 * Daí esta função existir separada: é ela, e não `detalhes()`, que decide
 * se o botão aparece.
 */
export async function podeComprar(produto: string = PRODUTO_PADRAO): Promise<boolean> {
  try {
    const pedido = new PaymentRequest(
      [{ supportedMethods: METODO, data: { sku: produto } }],
      { total: { label: "Assinatura", amount: { currency: "BRL", value: "0" } } },
    );
    return await pedido.canMakePayment();
  } catch {
    return false;
  }
}

/**
 * O que a loja respondeu sobre o produto — `null` quando não respondeu nada.
 *
 * O PORQUÊ da falha fica no console, e só nele. Antes ele também ia para
 * a tela de assinar, em letra miúda: quem estava comprando lia "Unable to
 * execute getDetails; clientAppUnavailable" e o id interno do produto por
 * cima da oferta. Diagnóstico é para quem conserta, e essa tela é para
 * quem paga.
 *
 * As duas causas continuam separadas na mensagem porque pedem coisas
 * opostas de quem for consertar: "a chamada falhou" é problema de app ou
 * de conta; "a loja respondeu sem este item" é cadastro no Play Console —
 * ou só o produto ainda propagando, o que leva até 24h depois de criado.
 */
export async function detalhes(produto: string = PRODUTO_PADRAO): Promise<DetalhesDoItem | null> {
  const loja = await servico();
  if (!loja) return null;

  let lista: DetalhesDoItem[];
  try {
    lista = await loja.getDetails([produto]);
  } catch (err) {
    console.error("[nhomi] getDetails falhou:", err);
    return null;
  }

  const achado = lista.find((i) => i.itemId === produto);
  if (achado) return achado;

  /* Os ids que VIERAM são o que denuncia id divergente na hora — sem
     eles, "não achei" e "achei outro" são a mesma linha de log. */
  console.warn("[nhomi]", lista.length
    ? `A loja respondeu, mas com outro produto: ${lista.map((i) => i.itemId).join(", ")}.`
      + ` O esperado é ${produto}.`
    : `A loja não devolveu nenhum plano para ${produto}.`);
  return null;
}

/**
 * O que a loja já vendeu para esta conta Google neste aparelho.
 *
 * É a RESTAURAÇÃO, e ela não é um extra. Quem reinstala o app, troca de
 * celular ou limpa os dados continua tendo pago — e sem isso abriria o
 * app como grátis, com a Play cobrando em silêncio no mês seguinte.
 * Nenhuma reclamação é pior que essa.
 *
 * Também é o conserto da compra órfã: quando o pagamento passa mas o app
 * fecha antes de confirmar com o servidor, é esta chamada, na abertura
 * seguinte, que acha o token perdido.
 */
export async function comprasAtivas(): Promise<{ itemId: string; purchaseToken: string }[]> {
  const loja = await servico();
  if (!loja) return [];
  return loja.listPurchases().catch(() => []);
}

export type Resultado =
  | { ok: true; token: string; concluir: (deuCerto: boolean) => Promise<void> }
  | { ok: false; motivo: "indisponivel" | "cancelada" | "falhou"; detalhe?: string };

/**
 * Abre a folha de pagamento da Play.
 *
 * O `total` abaixo é DECORATIVO e isso engana muita gente: a Play cobra
 * o preço do produto cadastrado no console, não o valor que vai aqui. A
 * Payment Request API exige um total porque foi desenhada para carrinho
 * de loja; com a Play como método, ela ignora. Zerar deixa claro que
 * ninguém deve tentar manter este número em dia.
 *
 * `concluir` existe porque a folha continua aberta depois do pagamento,
 * esperando alguém dizer se deu certo — e quem sabe disso é o SERVIDOR,
 * depois de perguntar à Play quanto vale o token. Fechar como sucesso
 * aqui, antes da confirmação, seria dizer "pronto" para quem talvez não
 * tenha virado assinante.
 */
export async function comprar(produto: string = PRODUTO_PADRAO): Promise<Resultado> {
  if (!(await disponivel())) return { ok: false, motivo: "indisponivel" };

  try {
    const pedido = new PaymentRequest(
      [{ supportedMethods: METODO, data: { sku: produto } }],
      { total: { label: "Assinatura", amount: { currency: "BRL", value: "0" } } },
    );

    if (!(await pedido.canMakePayment())) return { ok: false, motivo: "indisponivel" };

    const resposta = await pedido.show();
    const token = String(
      (resposta.details as { purchaseToken?: string } | undefined)?.purchaseToken ?? "",
    );

    if (!token) {
      await resposta.complete("fail");
      return { ok: false, motivo: "falhou", detalhe: "A loja não devolveu o comprovante." };
    }

    return {
      ok: true,
      token,
      concluir: async (deuCerto) => {
        await resposta.complete(deuCerto ? "success" : "fail").catch(() => {});
      },
    };
  } catch (err) {
    /* Fechar a folha é `AbortError`, e não é falha — é alguém que olhou
       o preço e mudou de ideia. Tratar como erro encheria a tela de
       vermelho para quem não fez nada errado. */
    if (err instanceof DOMException && err.name === "AbortError") {
      return { ok: false, motivo: "cancelada" };
    }
    return { ok: false, motivo: "falhou", detalhe: (err as Error)?.message };
  }
}
